// Recurring-series view and "this and future occurrences" commands (PB-04-02; PD-472 – PD-474).
import { z } from 'zod';
import { RecurrenceSchema, type RecurrenceDto, type SkippedOccurrenceDto } from './recurrence.ts';
import { UpdateVisitRequest, type VisitSummary } from './visits.ts';

/**
 * Edit the chosen occurrence and every later open occurrence of its series. Same fields and rules as
 * a single-visit edit; `expectedVersion` is the chosen occurrence's. A schedule is entered for the
 * chosen occurrence and applied to each later one at the same local times and day offset.
 */
export const SeriesEditRequest = UpdateVisitRequest;
export type SeriesEditRequest = z.infer<typeof SeriesEditRequest>;

export const SeriesCancelRequest = z.object({
  expectedVersion: z.number().int().min(1),
  reason: z.string().trim().min(1, 'v.required').max(500, 'v.tooLong'),
});
export type SeriesCancelRequest = z.infer<typeof SeriesCancelRequest>;

/** Preview the occurrence dates before creating a series (nothing is stored). */
export const RecurrencePreviewRequest = z.object({
  siteId: z.uuid(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date'),
  recurrence: RecurrenceSchema,
});
export type RecurrencePreviewRequest = z.infer<typeof RecurrencePreviewRequest>;

export interface RecurrencePreviewResponse {
  readonly dates: readonly string[];
  readonly excluded: readonly string[];
  readonly skipped: readonly SkippedOccurrenceDto[];
  readonly maxOccurrences: number;
}

export interface SeriesOccurrenceOutcome {
  readonly visitId: string;
  readonly date: string;
  readonly outcome: 'updated' | 'unchanged' | 'cancelled' | 'failed';
  readonly message?: string;
  readonly materialFields?: readonly string[];
  readonly approvalsInvalidated?: number;
}

export interface SeriesCommandResponse {
  /** The series the changed occurrences belong to now (a new one after a split). */
  readonly seriesId: string;
  readonly split: boolean;
  readonly fromDate: string;
  /** Earlier occurrences left exactly as they were. */
  readonly kept: number;
  readonly occurrences: readonly SeriesOccurrenceOutcome[];
}

export interface VisitSeriesSegment {
  readonly id: string;
  readonly parentSeriesId: string | null;
  readonly startsOn: string | null;
  readonly endedFrom: string | null;
  readonly endedReason: 'split' | 'cancelled' | null;
  readonly rule: RecurrenceDto | Record<string, unknown>;
  readonly skipped: readonly SkippedOccurrenceDto[];
  readonly createdAt: string;
}

export interface VisitSeriesOccurrence extends VisitSummary {
  readonly occurrenceDate: string | null;
}

/** All parts of one recurring plan (the original series and any splits), in date order. */
export interface VisitSeriesResponse {
  readonly rootId: string;
  readonly siteId: string;
  readonly timeZone: string;
  readonly segments: readonly VisitSeriesSegment[];
  readonly occurrences: readonly VisitSeriesOccurrence[];
  /** Occurrences the caller may not see (counted, never described). */
  readonly hidden: number;
}
